import { useInjection } from 'inversify-react';
import { useCallback } from 'react';

import { ProjectsFacade } from '../facade/projects.facade';
import { Page } from '../models/project.model';
import { TYPES } from '../projects.types';
import { IProjectsStore, ProjectStoreInjector } from '../store/projects.store';

export function useProjects(): {
  project: IProjectsStore['project']['currentProject'];
  pages: IProjectsStore['pages']['allPages'];
  libraryFonts: IProjectsStore['fonts']['library'];
  googleFonts: IProjectsStore['fonts']['googleFonts'];
  loadProject: (
    id: string | number,
    type: 'design' | 'template'
  ) => Promise<IProjectsStore['project']['currentProject']>;
  loadPage: (id: string | number) => Promise<Page>;
  loadPublicProject: (
    id: string | number,
    type: 'design' | 'template'
  ) => Promise<IProjectsStore['project']['currentProject']>;
  loadPublicPage: (id: string | number) => Promise<Page>;
} {
  const facade = useInjection<ProjectsFacade>(TYPES.ProjectsFacade);

  const useProjectStore = useInjection<ProjectStoreInjector>(TYPES.ProjectsStore);

  const project = useProjectStore((state) => state.project.currentProject);
  const pages = useProjectStore((state) => state.pages.allPages);
  const libraryFonts = useProjectStore((state) => state.fonts.library);
  const googleFonts = useProjectStore((state) => state.fonts.googleFonts);

  const loadProject = useCallback(
    async (id: string | number, type: 'design' | 'template') => {
      return await facade.loadProject(id, type);
    },
    [facade]
  );

  const loadPublicProject = useCallback(
    async (id: string | number, type: 'design' | 'template') => {
      return await facade.loadPublicProject(id, type);
    },
    [facade]
  );

  const loadPage = useCallback(
    async (id: string | number) => {
      return await facade.loadPage(id);
    },
    [facade]
  );

  const loadPublicPage = useCallback(
    async (id: string | number) => {
      return await facade.loadPublicPage(id);
    },
    [facade]
  );

  return {
    project,
    pages,
    libraryFonts,
    googleFonts,
    loadProject,
    loadPage,
    loadPublicProject,
    loadPublicPage,
  };
}
